import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  setStationCode,
  selectStationCode,
} from '../store/slices/shiftPlanSlice';
import { getStationCodeFromUrl } from '../utils/urlStationCode';

/**
 * useResolveStationCode — FALLBACK hook for stationCode resolution.
 *
 * The primary resolution now happens synchronously at store creation time
 * (see store/index.ts → getStationCodeFromUrl) so that stationCode is
 * available before any component mounts or useEffect fires.
 *
 * This hook is retained as a safety net for edge cases where the URL may
 * change after initial load (e.g., parent frame updates the query string
 * mid-session) or if the store was created in a test environment without
 * a real window.location.
 *
 * Flow:
 *   1. If shiftPlanSlice already has stationCode (from store init or prior
 *      dispatch), do nothing.
 *   2. Otherwise read ?stationCode= from window.location.search
 *      (before the # in a HashRouter URL — works for all routes).
 *   3. Dispatch to shiftPlanSlice.
 */
export const useResolveStationCode = (): string | null => {
  const dispatch = useAppDispatch();
  const stationCode = useAppSelector(selectStationCode);

  useEffect(() => {
    if (!stationCode) {
      const stationCodeParam = getStationCodeFromUrl();
      if (stationCodeParam) {
        dispatch(setStationCode({ stationCode: stationCodeParam }));
      }
    }
  }, [dispatch, stationCode]);

  return stationCode;
};
